import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Clock, ExternalLink, ChevronDown, ChevronUp, AlertTriangle,
  CheckCircle, AlertCircle, HelpCircle, Shield, Scale, Search,
  UserCheck, FileText, Skull, Heart
} from 'lucide-react';
import { formatDate } from '@/utils/dateUtils';

// Icon mapping for update types
const UPDATE_ICONS = {
  arrest: Scale,
  charges_filed: Scale,
  conviction: Scale,
  sentencing: Scale,
  poi_identified: UserCheck,
  dna_recovered: FileText,
  remains_found: Skull,
  remains_identified: CheckCircle,
  found_safe: Heart,
  found_deceased: Skull,
  case_closed: CheckCircle,
  search_conducted: Search,
  default: FileText
};

// Color mapping for update types
const UPDATE_COLORS = {
  arrest: 'bg-red-500',
  charges_filed: 'bg-red-500',
  conviction: 'bg-red-600',
  sentencing: 'bg-red-600',
  found_safe: 'bg-green-500',
  found_deceased: 'bg-slate-600',
  remains_found: 'bg-purple-500',
  remains_identified: 'bg-purple-600',
  case_closed: 'bg-slate-500',
  dna_recovered: 'bg-blue-500',
  poi_identified: 'bg-orange-500',
  default: 'bg-trust-blue-500'
};

// Confidence badge component
export function ConfidenceBadge({ level, badge }) {
  const colors = {
    verified: 'bg-green-100 text-green-800 border-green-300',
    reported: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    unconfirmed: 'bg-gray-100 text-gray-600 border-gray-300'
  };
  
  const icons = {
    verified: <CheckCircle className="w-3 h-3" />,
    reported: <AlertCircle className="w-3 h-3" />,
    unconfirmed: <HelpCircle className="w-3 h-3" />
  };
  
  return (
    <Badge variant="outline" className={`${colors[level] || colors.reported} text-xs flex items-center gap-1`}>
      {icons[level]}
      {badge?.label || level}
    </Badge>
  );
}

// Latest Update Card - shown at top of case page
export function LatestUpdateCard({ update, onViewAll }) {
  if (!update) return null;
  
  const Icon = UPDATE_ICONS[update.update_type] || UPDATE_ICONS.default;
  const bgColor = UPDATE_COLORS[update.update_type] || UPDATE_COLORS.default;
  
  return (
    <Card className="border-l-4 border-l-trust-blue-500 bg-gradient-to-r from-trust-blue-50/50 to-transparent dark:from-trust-blue-950/20">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`w-10 h-10 rounded-lg ${bgColor} flex items-center justify-center flex-shrink-0`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="font-semibold text-sm">{update.update_type_label}</span>
              <ConfidenceBadge level={update.confidence_level} badge={update.confidence_badge} />
              {update.safety_wording_flag && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-xs">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Contains Allegations
                </Badge>
              )}
            </div>
            
            <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">
              {update.summary}
            </p>
            
            <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDate(update.date_of_update)}
              </span>
              <span className="text-slate-400">|</span>
              <span>{update.source_type_label}</span>
              {update.source_link && (
                <a 
                  href={update.source_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-trust-blue-600 hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  Source
                </a>
              )}
            </div>
          </div>
          
          {onViewAll && (
            <Button variant="ghost" size="sm" onClick={onViewAll} className="flex-shrink-0">
              View All
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Single Update Item for timeline
export function UpdateTimelineItem({ update, isExpanded, onToggle }) {
  const Icon = UPDATE_ICONS[update.update_type] || UPDATE_ICONS.default;
  const bgColor = UPDATE_COLORS[update.update_type] || UPDATE_COLORS.default;
  
  return (
    <div className="relative pl-8 pb-6 last:pb-0">
      {/* Timeline line */}
      <div className="absolute left-[11px] top-8 bottom-0 w-0.5 bg-slate-200 dark:bg-slate-700 last:hidden" />
      
      {/* Timeline dot */}
      <div className={`absolute left-0 w-6 h-6 rounded-full ${bgColor} flex items-center justify-center`}>
        <Icon className="w-3 h-3 text-white" />
      </div>
      
      <div className="bg-white dark:bg-slate-800 rounded-lg border p-4 shadow-sm">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{update.update_type_label}</span>
            <ConfidenceBadge level={update.confidence_level} badge={update.confidence_badge} />
            {update.is_major_update && (
              <Badge className="bg-red-500 text-white text-xs">Major</Badge>
            )}
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {formatDate(update.date_of_update)}
          </span>
        </div>
        
        {update.safety_wording_flag && (
          <div className="flex items-center gap-2 mb-2 p-2 bg-amber-50 dark:bg-amber-950/30 rounded text-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="w-4 h-4" />
            <span>This update contains allegations or unverified claims. Read source for context.</span>
          </div>
        )}
        
        <p className="text-sm text-slate-700 dark:text-slate-300 mb-3">
          {update.summary}
        </p>
        
        {update.details && (
          <>
            <button 
              onClick={onToggle}
              className="text-xs text-trust-blue-600 hover:underline flex items-center gap-1 mb-2"
            >
              {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {isExpanded ? 'Show less' : 'Read more'}
            </button>
            
            {isExpanded && (
              <div className="text-sm text-slate-600 dark:text-slate-400 mb-3 p-3 bg-slate-50 dark:bg-slate-900 rounded">
                {update.details}
              </div>
            )}
          </>
        )}
        
        <div className="flex items-center gap-4 text-xs text-muted-foreground border-t pt-3 flex-wrap">
          <span className="flex items-center gap-1">
            <Shield className="w-3 h-3" />
            {update.source_type_label}
          </span>
          {update.source_link && (
            <a 
              href={update.source_link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-trust-blue-600 hover:underline flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" />
              {update.source_title || 'View Source'}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// Updates Timeline Component
export function UpdatesTimeline({ updates, maxVisible = 5 }) {
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [showAll, setShowAll] = useState(false);
  
  const toggleExpanded = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  
  if (!updates || updates.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
        <p>No updates available for this case.</p>
      </div>
    );
  }
  
  const visibleUpdates = showAll ? updates : updates.slice(0, maxVisible);
  
  return (
    <div className="space-y-2">
      <div className="relative">
        {visibleUpdates.map((update) => (
          <UpdateTimelineItem
            key={update.id}
            update={update}
            isExpanded={expandedIds.has(update.id)}
            onToggle={() => toggleExpanded(update.id)}
          />
        ))}
      </div>
      
      {updates.length > maxVisible && (
        <Button 
          variant="ghost" 
          className="w-full" 
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? (
            <>
              <ChevronUp className="w-4 h-4 mr-2" />
              Show Less
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4 mr-2" />
              Show {updates.length - maxVisible} More Updates
            </>
          )}
        </Button>
      )}
    </div>
  );
}

// Key Developments Chips
export function KeyDevelopmentsChips({ updates }) {
  if (!updates || updates.length === 0) return null;
  
  // Get unique major update types
  const majorUpdates = updates.filter(u => u.is_major_update);
  const uniqueTypes = [...new Set(majorUpdates.map(u => u.update_type))];
  
  if (uniqueTypes.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-2">
      {uniqueTypes.map(type => {
        const update = majorUpdates.find(u => u.update_type === type);
        const Icon = UPDATE_ICONS[type] || UPDATE_ICONS.default;
        const bgColor = UPDATE_COLORS[type] || UPDATE_COLORS.default;
        
        return (
          <Badge 
            key={type} 
            className={`${bgColor} text-white flex items-center gap-1 px-3 py-1`}
          >
            <Icon className="w-3 h-3" />
            {update.update_type_label}
          </Badge>
        );
      })}
    </div>
  );
}

export default {
  LatestUpdateCard,
  UpdatesTimeline,
  KeyDevelopmentsChips,
  ConfidenceBadge
};
